import { useEffect, useMemo } from 'react';
import { LockAuditLog, LockLogsListTypes } from './types';
import useQueryState from '../table/hooks/use-query-state';
import useGetList from '../../hooks/use-get-list';
import useFilters from '../table/hooks/use-filters';
import formatLogsList from './helpers/format-logs-list';
import { actions } from './variables/lists';
import { PermissionEnum } from '../../base-types';
import { Filters, SchemaFilterType } from '../table/types';
import PermissionDenied from '../icons/permission-denied';
import IconLoader from '../icons/loader';
import Table from '../table/table';

import cn from './styles.module.css';

export default function SecurityLockLogs({
  lockItemAuditLogs,
  lockItemAuditLogsPagination,
  getList,
  schema,
  filtersSchema,
  activePermissions,
  columns,
  columnsLabels,
  isLoading,
  isFetching,
  lockId,
}: LockLogsListTypes) {
  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    schema,
    true,
  );

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<LockAuditLog>({
      getList,
      onFilterChange,
      onSortChange,
      schema,
      withoutChangeSearchParams: true,
    });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatLogsList(lockItemAuditLogs as LockAuditLog[]);

    return data;
  }, [lockItemAuditLogs, sort, filters]);

  useEffect(() => {
    setValues('action', actions);
  }, []);

  useEffect(() => {
    if (
      filters &&
      !lockItemAuditLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      lockId
    ) {
      const nextFilters: Filters = {
        ...filters,
        take: {
          value: ['20'],
          type: SchemaFilterType.STRING,
        },
        lockId: {
          value: [lockId],
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: nextFilters,
      });
    }
  }, [filters, lockItemAuditLogs, activePermissions, lockId]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) {
    content = <PermissionDenied />;
  } else if (activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) {
    if (isLoading) {
      content = (
        <div className={cn.locksLoader}>
          <IconLoader />
        </div>
      );
    } else {
      content = (
        <div className={cn.lockItemLogsTable}>
          <Table
            columns={columns}
            data={formattedData || []}
            onSort={() => {}}
            filters={filters}
            sort={sort}
            columnsLabels={columnsLabels}
            onFilterChange={(filterValues) => {
              const nextFilters: Filters = {
                ...filterValues,
                take: {
                  value: ['20'],
                  type: SchemaFilterType.STRING,
                },
                lockId: {
                  value: [lockId as string],
                  type: SchemaFilterType.STRING,
                },
              };

              onFilter({
                ...nextFilters,
              });
            }}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isFetching}
            take="20"
            setPage={(data) => {
              const filterValues: Filters = {
                ...filters,
                take: {
                  value: ['20'],
                  type: SchemaFilterType.STRING,
                },
                lockId: {
                  value: [lockId as string],
                  type: SchemaFilterType.STRING,
                },
              };

              setPage({ ...data, filters: filterValues });
            }}
            pagination={lockItemAuditLogsPagination}
            error={errorList}
            withVerticalScroll
            withScroll
          />
        </div>
      );
    }
  }

  return content;
}
